import { DatePipe, NgTemplateOutlet } from "@angular/common";
import { Component, computed, inject, input, output, signal, type TemplateRef, viewChild } from "@angular/core";
import { NgbModal, type NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import saveAs from "file-saver";
import { filter, forkJoin, tap } from "rxjs";

import { TranslateTokenPipe } from "../../../../../core/pipes";
import { AdaaHelper, isDsV3 } from "../../../../../core/utils";
import { Constants } from "../../../../constants/constants";
import {
  AuditTrailDiffModelType,
  CardEntryInfoType,
  CommonKpiModelType,
  DimensionValue,
  KpiAnnualWeightsModelType,
  KpiLinkType,
  type WorkflowDiffInputType,
  type WorkflowItemDiffModelType,
} from "../../../../models";
import { FilesApiService, MetricApiService, PeriodApiService, WorkflowsApiService } from "../../../../services";
import { AuditTrailApiService } from "../../../../services/audit-trail-api-service";
import {
  AgmScreenComponent,
  AuditDataEntryScreenComponent,
  DataEntryInfoScreenComponent,
  DataEntryScreenComponent,
  GovProjectScreenComponent,
  IntpScreenComponent,
  KpiAnnualWeightsScreenComponent,
  KpiScreenComponent,
  MetricActualValueScreenComponent,
  MetricsScreenComponent,
  NtkpiCardScreenComponent,
  SimpleScreenComponent,
  TpMilestoneScreenComponent,
  TpScreenComponent,
  TransformationalAgreementsScreenComponent,
} from "./screens";
import { ScreenType } from "./wf-difference.types";
import { isKpiWorkflow, isTransformationalAgreement } from "./wf-difference.utils";

@Component({
  selector: "adaa-wf-difference",
  templateUrl: "./wf-difference.html",
  styleUrl: "./wf-difference.scss",
  imports: [
    TranslatePipe,
    DatePipe,
    NgTemplateOutlet,
    TranslateTokenPipe,
    KpiScreenComponent,
    DataEntryScreenComponent,
    AuditDataEntryScreenComponent,
    KpiAnnualWeightsScreenComponent,
    MetricActualValueScreenComponent,
    NtkpiCardScreenComponent,
    SimpleScreenComponent,
    TpMilestoneScreenComponent,
    TpScreenComponent,
    AgmScreenComponent,
    TransformationalAgreementsScreenComponent,
    MetricsScreenComponent,
    DataEntryInfoScreenComponent,
    IntpScreenComponent,
    GovProjectScreenComponent,
  ],
})
export class WfDifferenceComponent {
  private readonly _modalService = inject(NgbModal);
  private readonly _translateService = inject(TranslateService);
  private readonly _workflowsApiService = inject(WorkflowsApiService);
  private readonly _auditTrailApiService = inject(AuditTrailApiService);
  private readonly _metricApiService = inject(MetricApiService);
  private readonly _periodApiService = inject(PeriodApiService);
  private readonly _filesApiService = inject(FilesApiService);

  isAuditTrail = input.required<boolean>();
  showOverrideDecision = input<boolean>(false);
  overrideDecisionClicked = output<void>();

  actionTypeId = signal<number | undefined>(undefined);
  actionItemTypes = signal<{ id: number; nameAE: string; nameEN: string }[]>([]);
  objectTypes = signal<{ id: number; nameAE: string; nameEN: string }[]>([]);
  displayData = signal<boolean>(true);
  showOnlyNew = signal<boolean>(false);
  isNew = signal<boolean>(false);
  screenType = signal<ScreenType | undefined>(undefined);
  isForecastedValues = signal<boolean>(false);
  queryInput = signal<WorkflowDiffInputType | { trailId: number } | undefined>(undefined);
  data = signal<WorkflowItemDiffModelType | AuditTrailDiffModelType | KpiAnnualWeightsModelType[] | undefined>(
    undefined
  );
  viewOnlyDifferences = signal<boolean>(false);
  isNewEntries = signal<boolean>(false);

  titleKey = computed(() => {
    if (this.isAuditTrail()) return "auditrail.audittrail_detail_info";
    if (this.showOnlyNew()) return "communication.difference.view_object";
    return "communication.difference.title";
  });
  showViewOnlyDiffSwitch = computed(() => {
    const actionTypeId = this.auditDiff()?.actionTypeId;
    const actionDetail = this.auditDiff()?.actionDetail;
    const isUpdateAction = Constants.NOTIFICATION_ACTION_TYPES.UPDATE === actionDetail;
    const isUpdateActionType = Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.UPDATE === actionTypeId;
    const isApprovalActionType = Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.APPROVAL === actionTypeId;
    const isRejectionActionType = Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.REJECTION === actionTypeId;

    if (this.isForecastedValues()) return false;
    if (isUpdateActionType) return true;
    return (isApprovalActionType || isRejectionActionType) && isUpdateAction;
  });
  itemType = computed(() => {
    return (this.data() as WorkflowItemDiffModelType).itemType ?? (this.data() as AuditTrailDiffModelType).objectType;
  });
  kpiAnnualWeights = computed(() => {
    return this.data() as KpiAnnualWeightsModelType[];
  });
  isObjectKpiAnnualWeights = computed(() => {
    if (this._isWorkflowDiffInputType(this.queryInput())) {
      return (
        Object.values(Constants.CONSTANT_KPI_ANNUAL_WEIGHTS).indexOf(
          (this.queryInput() as WorkflowDiffInputType)?.itemTypeId
        ) >= 0
      );
    }
    return false;
  });
  wfProcessCtlId = computed(() => {
    if (this._isWorkflowDiffInputType(this.queryInput())) {
      return (this.queryInput() as WorkflowDiffInputType)?.wfProcessCtlId;
    }
    return undefined;
  });
  newObject = computed(() => {
    if (this.isAuditTrail()) return this.auditDiff()?.newVersion;
    return this.wfDiff()?.newVersion;
  });
  oldObject = computed(() => {
    if (this.isAuditTrail()) return this.auditDiff()?.oldVersion;
    return this.wfDiff()?.oldVersion;
  });
  wfDiff = computed(() => this.data() as WorkflowItemDiffModelType | undefined);
  auditDiff = computed(() => this.data() as AuditTrailDiffModelType | undefined);
  auditActionTypeText = computed(() => {
    const actionTypeId = this.auditDiff()?.actionTypeId;
    return AdaaHelper.getItemValueByToken(
      this.actionItemTypes().find(({ id }) => id === actionTypeId),
      "name"
    );
  });
  auditObjectTypeText = computed(() => {
    const objectType = this.auditDiff()?.objectType;
    const actionType = this.auditDiff()?.actionTypeId;
    if (objectType) {
      return AdaaHelper.getItemValueByToken(
        this.objectTypes().find(({ id }) => id === objectType),
        "name"
      );
    }
    if (actionType) return this._translateService.instant(`audit_actions.${actionType}`);
    return "";
  });

  content = viewChild.required<TemplateRef<unknown>>("content");
  body = viewChild.required<TemplateRef<unknown>>("body");

  readonly constants = {
    dateFormat: Constants.dateTime,
    uaeTimezone: Constants.uaeTimezone,
  };

  modalRef: NgbModalRef;

  public load(input: WorkflowDiffInputType | { trailId: number }) {
    this.queryInput.set(input);
    this._getDiff();
  }

  public open(input: WorkflowDiffInputType | { trailId: number }) {
    this.load(input);

    const inputWf = input as WorkflowDiffInputType;
    if (isTransformationalAgreement(inputWf.itemTypeId)) return;

    this.modalRef = this._modalService.open(this.content(), {
      animation: true,
      scrollable: false,
      ariaLabelledBy: this._translateService.instant("workflow.workflow_history"),
      centered: true,
      size: "xl",
      beforeDismiss: () => {
        this._resetState();

        return true;
      },
    });

    this.modalRef.result.then(() => this._resetState()).catch(() => this._resetState());
  }

  private _resetState() {
    this.objectTypes.set([]);
    this.actionItemTypes.set([]);
    this.displayData.set(true);
    this.showOnlyNew.set(false);
    this.isNew.set(false);
    this.screenType.set(undefined);
    this.queryInput.set(undefined);
    this.data.set(undefined);
    this.viewOnlyDifferences.set(false);
    this.isNewEntries.set(false);
  }

  public showWfInfo() {
    const actionTypeId = this.auditDiff()?.actionTypeId;

    switch (actionTypeId) {
      case Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.APPROVAL:
      case Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.REJECTION:
        return true;
      default:
        return false;
    }
  }

  private _setupAuditTrail() {
    const actionTypeId = this.auditDiff()?.actionTypeId;
    this.actionTypeId.set(actionTypeId);

    switch (actionTypeId) {
      case Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.CREATE:
      case Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.DELETE:
      case Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.UPDATE:
      case Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.APPROVAL:
      case Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.REJECTION:
      case Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.PUBLISH:
      case Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.SORT:
        this.displayData.set(true);
        this.showOnlyNew.set(actionTypeId === Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.CREATE);
        if (
          this.isAuditTrail() &&
          (actionTypeId === Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.APPROVAL ||
            actionTypeId === Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.REJECTION) &&
          this.auditDiff()?.actionDetail === Constants.NOTIFICATION_ACTION_TYPES.CREATE
        ) {
          this.showOnlyNew.set(true);
        }
        this.isNew.set(actionTypeId === Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.CREATE);
        break;
      default:
        this.displayData.set(false);
    }

    this.data.update((value) => {
      let newVersion = (value as AuditTrailDiffModelType).newVersion;
      let oldVersion = (value as AuditTrailDiffModelType).oldVersion;

      if (!AdaaHelper.isDefined(newVersion?.id) && AdaaHelper.isDefined(oldVersion?.id)) {
        newVersion = { ...oldVersion };
      } else if (AdaaHelper.isDefined(newVersion?.id) && !AdaaHelper.isDefined(oldVersion?.id)) {
        oldVersion = { ...newVersion };
      }

      return {
        ...(value as AuditTrailDiffModelType),
        newVersion: newVersion ?? oldVersion,
        oldVersion: oldVersion ?? newVersion,
      };
    });

    if (this._hasNoVersionData()) {
      this.displayData.set(false);
      return;
    }

    this._selectAuditScreen();
  }

  private _hasNoVersionData(): boolean {
    if (this.auditDiff()?.missingData) return true;
    if (AdaaHelper.isDefined(this.newObject())) return false;
    if (AdaaHelper.isDefined(this.oldObject())) return false;
    return true;
  }

  private _setupWorkflow() {
    const wfActionType = (this.queryInput() as WorkflowDiffInputType).wfActionType;
    const newVersion = this.wfDiff()!.newVersion;
    const oldVersion = this.wfDiff()!.oldVersion;
    const isKpi = AdaaHelper.isDefined(newVersion?.kpiType) || AdaaHelper.isDefined(oldVersion?.kpiType);

    this.showOnlyNew.set(
      wfActionType === Constants.NOTIFICATION_ACTION_TYPES.CREATE ||
        wfActionType === Constants.NOTIFICATION_ACTION_TYPES.DELETE
    );
    this.isNew.set(wfActionType === Constants.NOTIFICATION_ACTION_TYPES.CREATE);

    this._selectWfScreen(isKpi);
  }

  private _selectAuditScreen() {
    const actionType = this.auditDiff()?.actionDetail;
    const objectType = this.auditDiff()?.objectType;

    if (isKpiWorkflow(objectType!)) {
      this._selectAuditKpiScreen(actionType);
      return;
    }

    if (actionType === Constants.NOTIFICATION_ACTION_TYPES.DATA_ENTRY && isDsV3()) {
      this.isNewEntries.set(true);
      this.screenType.set("data-entry");
      return;
    }

    this.screenType.set(this._auditScreenForObjectType(objectType));
  }

  private _selectAuditKpiScreen(actionType: number | undefined) {
    switch (actionType) {
      case Constants.NOTIFICATION_ACTION_TYPES.DATA_ENTRY:
      case Constants.NOTIFICATION_ACTION_TYPES.FORECASTED_VALUES:
        this.isForecastedValues.set(actionType === Constants.NOTIFICATION_ACTION_TYPES.FORECASTED_VALUES);
        this.screenType.set("data-entry");
        break;
      case Constants.NOTIFICATION_ACTION_TYPES.AUDIT_DATA_ENTRY:
        this.screenType.set("audit-data-entry");
        break;
      default:
        this.screenType.set("kpi");
        break;
    }
  }

  private _auditScreenForObjectType(objectType: number | undefined): ScreenType {
    switch (objectType) {
      case Constants.CONSTANT_DATA_ENTRY_INFO:
      case Constants.CONSTANT_BENCHMARK_DATA_ENTRY_INFO:
        return "data-entry-info";
      case Constants.CONSTANT_NTKPI_CHALLENGE_ANALYSIS:
      case Constants.CONSTANT_NTKPI_SUCCESS_STORY:
        return "ntkpi-card";
      case Constants.CONSTANT_NATIONAL_PROJECTS_MILESTONE:
        return "tp-milestone";
      case Constants.CONSTANT_NATIONAL_PROJECTS:
        return "tp";
      case Constants.CONSTANT_WORKFLOW.INTERNATIONAL_PROJECT:
        return "intp";
      case Constants.METRICS_TYPE_ID.DIMENSION:
        return "metrics";
      case Constants.CONSTANT_WORKFLOW.ANNUAL_PROJECT_PROJECTS:
      case Constants.CONSTANT_WORKFLOW.ANNUAL_PROJECT_MILESTONES:
      case Constants.CONSTANT_ANNUAL_MEETINGS_PROJECT:
      case Constants.CONSTANT_ANNUAL_MEETINGS_MILESTONE:
        return "agm";
      case Constants.GOV_TRACKS.AI:
      case Constants.GOV_MILESTONES.AI:
      case Constants.GOV_TASKS.AI:
      case Constants.AI_AGENTS:
        return "gov-project";
      default:
        return "simple";
    }
  }

  private _selectWfScreen(isKpi: boolean = true) {
    const { wfActionType, itemTypeId } = this.queryInput() as WorkflowDiffInputType;

    if (isKpi) {
      switch (wfActionType) {
        case Constants.NOTIFICATION_ACTION_TYPES.DATA_ENTRY:
          this.screenType.set("data-entry");
          break;
        case Constants.NOTIFICATION_ACTION_TYPES.AUDIT_DATA_ENTRY:
          this.screenType.set("audit-data-entry");
          break;
        default:
          // a plain kpi review never falls back to the annual weights screen
          this.screenType.set("kpi");
          return;
      }
    } else if (wfActionType === Constants.NOTIFICATION_ACTION_TYPES.DATA_ENTRY && isDsV3()) {
      this.isNewEntries.set(true);
      this.screenType.set("data-entry");
    } else {
      this.screenType.set(this._wfScreenForItemType(itemTypeId));
    }

    if (this._isKpiAnnualWeightsItem(itemTypeId)) {
      this.screenType.set("kpi-annual-weights");
    }
  }

  private _wfScreenForItemType(itemTypeId: number | undefined): ScreenType {
    switch (itemTypeId) {
      case Constants.CONSTANT_WORKFLOW.PROJECT_MILESTONES:
        return "tp-milestone";
      case Constants.CONSTANT_WORKFLOW.NATIONAL_PROJECTS:
        return "tp";
      case Constants.CONSTANT_WORKFLOW.INTERNATIONAL_PROJECT:
      case Constants.CONSTANT_WORKFLOW.INTERNATIONAL_PROJECT_MILESTONE:
        return "intp";
      case Constants.CONSTANT_WORKFLOW.ANNUAL_PROJECT_PROJECTS:
      case Constants.CONSTANT_WORKFLOW.ANNUAL_PROJECT_MILESTONES:
        return "agm";
      case Constants.CONSTANT_WORKFLOW.METRIC_ACTUALS:
        return "metric-actual-value";
      case Constants.METRICS_TYPE_ID.DIMENSION:
        return "metrics";
      case Constants.CONSTANT_WORKFLOW.AITASK:
        return "gov-project";
      case Constants.CONSTANT_WORKFLOW.OBJECTIVE:
      case Constants.CONSTANT_WORKFLOW.ACTIVITY:
      case Constants.CONSTANT_WORKFLOW.INITIATIVE:
      case Constants.CONSTANT_WORKFLOW.SERVICE:
      default:
        return "simple";
    }
  }

  private _isKpiAnnualWeightsItem(itemTypeId: number | undefined): boolean {
    return Object.values(Constants.CONSTANT_KPI_ANNUAL_WEIGHTS).includes(itemTypeId as number);
  }

  private _getDiff() {
    if (this.isAuditTrail()) return this._getAuditTrailDiff();

    if (this.isObjectKpiAnnualWeights()) {
      const { wfProcessCtlId } = this.queryInput() as WorkflowDiffInputType;
      if (wfProcessCtlId) return this._getKpiAnnualWeightsDiff(wfProcessCtlId);
    }

    return this._getWorkflowDiff(this.queryInput() as WorkflowDiffInputType);
  }

  private _getAuditTrailDiff() {
    const { trailId } = this.queryInput() as { trailId: number };

    this._getAuditItemTypes();

    return this._auditTrailApiService
      .getDifferences(trailId)
      .pipe(filter((res) => !res.inError))
      .subscribe({
        next: (res) => this._applyAuditTrailDiff(res.responseData),
      });
  }

  private _applyAuditTrailDiff(diff: AuditTrailDiffModelType) {
    const periodId = diff.newVersion?.periodId;
    const kpiType = diff.newVersion?.kpiType;

    if (diff.objectType === Constants.CONSTANT_DATA_ENTRY_INFO && periodId) {
      this._getDataEntryPeriod(periodId, diff);
      return;
    }

    if (kpiType) {
      this._processKpi(diff);
      return;
    }

    this.data.set(diff);
    this._setupAuditTrail();
  }

  private _getKpiAnnualWeightsDiff(wfProcessCtlId: number) {
    return this._workflowsApiService
      .getKpiAnnualWeightsDifferences(wfProcessCtlId)
      .pipe(filter((res) => !res.inError))
      .subscribe({
        next: (res) => this.data.set(res.responseData.weights),
        complete: () => this._setupWorkflow(),
      });
  }

  private _getWorkflowDiff(input: WorkflowDiffInputType) {
    return this._workflowsApiService
      .getWorkflowDifferences(input)
      .pipe(filter((res) => !res.inError))
      .subscribe({
        next: (res) => this._applyWorkflowDiff(res.responseData),
      });
  }

  private _applyWorkflowDiff(diff: WorkflowItemDiffModelType) {
    const kpiType = diff.newVersion?.kpiType;

    if (kpiType) {
      this._processKpi(diff);
      return;
    }

    this.data.set(diff);

    if (this._hasNoVersionData()) {
      this.displayData.set(false);
      return;
    }

    if (isTransformationalAgreement(diff.itemType)) {
      this._download(this.newObject()!);
      return;
    }

    this._setupWorkflow();
  }

  private _getAuditItemTypes() {
    const request$ = [this._auditTrailApiService.getActionTypes(), this._auditTrailApiService.getObjectTypes()];

    forkJoin(request$).subscribe({
      next: ([actionTypesRes, objectTypesRes]) => {
        this.actionItemTypes.set([...actionTypesRes.responseData]);
        this.objectTypes.set([...objectTypesRes.responseData]);
      },
    });
  }

  private _isWorkflowDiffInputType(
    checkObj: WorkflowDiffInputType | { trailId: number } | undefined
  ): checkObj is WorkflowDiffInputType {
    const optionalObj = checkObj as WorkflowDiffInputType;
    return this._isObject(optionalObj) && optionalObj.itemId !== undefined && optionalObj.itemTypeId !== undefined;
  }

  private _isObject(obj: unknown) {
    return obj !== null && typeof obj === "object";
  }

  private _processKpi(data: WorkflowItemDiffModelType | AuditTrailDiffModelType) {
    const newObject = data.newVersion as CommonKpiModelType;
    const oldObject = data.oldVersion as CommonKpiModelType;
    const hasDimensions = !!newObject?.kpiDimensionSet?.length || !!oldObject?.kpiDimensionSet?.length;
    const hasLinks = !!newObject?.links || !!oldObject?.links;

    if (hasLinks) this._mapLinks(data);

    if (hasDimensions) {
      this._mapAndApplyDimensionValues(data).subscribe(() => {
        this.data.set(data);
        this.isAuditTrail() ? this._setupAuditTrail() : this._setupWorkflow();
      });
    } else {
      this.data.set(data);
      this.isAuditTrail() ? this._setupAuditTrail() : this._setupWorkflow();
    }
  }

  private _mapAndApplyDimensionValues(data: WorkflowItemDiffModelType | AuditTrailDiffModelType) {
    const versions = [data.newVersion as CommonKpiModelType, data.oldVersion as CommonKpiModelType];

    const ids = [
      ...new Set(
        versions
          .flatMap((v) => v?.kpiDimensionSet?.flatMap((set) => set.values?.map((val) => val.dimensionId)))
          .filter(Boolean)
      ),
    ];

    return this._metricApiService.getDimensionsValues(ids).pipe(
      tap((res) => {
        const allValues = res.responseData as DimensionValue[];

        versions.forEach((version) => {
          if (version) {
            version.kpiDimensionSet = version?.kpiDimensionSet?.map((set) => ({
              ...set,
              values: set.values?.map((v) => ({
                ...v,
                dimensionValueIds: v.dimensionValueIds?.map((id) => allValues.find((dv) => dv.id === id)!),
              })),
            }));
          }
        });
      })
    );
  }

  private _mapLinks(data: WorkflowItemDiffModelType | AuditTrailDiffModelType) {
    const versions = [data.newVersion, data.oldVersion] as CommonKpiModelType[];

    versions.forEach((version) => {
      const links = version?.links;
      if (!links) return;

      Object.entries(links).forEach(([key, arr]) => {
        const mapped = arr?.map((link: KpiLinkType) => {
          if (link.typeId === Constants.METRICS_TYPE_ID.DIMENSION) return link;
          return {
            ...link,
            nameAE: `${link.nameAE} [${link.typeNameAE}]`,
            nameEN: `${link.nameEN} [${link.typeNameEN}]`,
          };
        });

        version[key] = mapped;
      });
    });
  }

  private _getDataEntryPeriod(periodId: number, data: WorkflowItemDiffModelType | AuditTrailDiffModelType) {
    this._periodApiService.getById(periodId).subscribe({
      next: (res) => {
        if (data.newVersion) {
          (data.newVersion as CardEntryInfoType).period =
            `${res.responseData.day}/${res.responseData.month}/${res.responseData.year}`;
        }
        if (data.oldVersion) {
          (data.oldVersion as CardEntryInfoType).period =
            `${res.responseData.day}/${res.responseData.month}/${res.responseData.year}`;
        }
        this.data.set(data);
        this.isAuditTrail() ? this._setupAuditTrail() : this._setupWorkflow();
      },
    });
  }

  private _download(data: Record<string, unknown>) {
    data.downloading = true;
    this._filesApiService.download(data.itemAttachmentId as number, true).subscribe({
      next: (blob) => {
        saveAs(blob, data.fileName as string);
      },
      error: () => {
        data.downloading = false;
      },
      complete: () => {
        data.downloading = false;
      },
    });
  }
}
